import type { GgxTourDefinition } from './tour-types';

export const HOWTO_06: GgxTourDefinition = {
    key: '[HOWTO-06] Creating a simple Timezone Converter Bot',
    route: '/assets/pipeline-builder',
    estimatedMinutes: 8,
    prerequisite: {
        target: '[test-id="create-btn"]',
        message: 'Open the Agents page, then start this tour.',
    },
    intro: {
        headline: 'Build Your First Pipeline',
        subtitle:
            'A pipeline is the unit you build, test, and ship in GenGuardX. In this tour you ' +
            'will build one from scratch: a Timezone Converter Bot that takes a time in one ' +
            'timezone and returns it in another. Along the way you will see how a pipeline is ' +
            'wired from a model and a prompt, and how to test it live before you rely on it.',
    },
    outro: {
        title: 'Your first pipeline is live. Nicely done!',
        subtitle:
            'You built a working pipeline end to end: you named it, chose how it runs, gave it ' +
            'a model and a prompt, wired the logic together, and saved it. You are now on its ' +
            'Chats tab, which is where you talk to it. The friendliest next step is the Live ' +
            'Testing a pipeline tour: it walks you through a full chat session on a ready-made ' +
            'pipeline, from starting the session to asking a question, rating the reply and ' +
            'saving the transcript. Once you have seen how that works, come back to this Chats ' +
            'tab and ask your bot "What is 3 PM New York time in London?", then "and in ' +
            'Tokyo?" to see the memory you gave it at work. After that, refine the prompt, run ' +
            'evaluations, and add more tools. This same shape scales from a simple bot to a ' +
            'complex, multi-step assistant.',
        learned: [
            'A pipeline is **named, given a build type**, then a model and a prompt.',
            '**Wiring the logic** connects the pieces into something that runs.',
            'The **Chats tab** is where you talk to what you built.',
        ],
    },
    chapters: [
        {
            name: 'Create the pipeline',
            blurb: 'Start one and give it an identity',
            icon: 'grid',
            steps: [2, 3, 4],
        },
        {
            name: 'Choose how it runs',
            blurb: 'Set the build type and behavior',
            icon: 'compass',
            steps: [5, 6, 7, 8],
        },
        {
            name: 'Give it a brain',
            blurb: 'Add the model that powers it',
            icon: 'pulse',
            steps: [9, 10, 11],
        },
        {
            name: 'Write the prompt',
            blurb: 'Tell the model what to do',
            icon: 'search',
            steps: [12, 13, 14, 15, 16, 17],
        },
        {
            name: 'Wire it up and test',
            blurb: 'Connect the logic and try it live',
            icon: 'map',
            steps: [18, 19, 20],
        },
    ],
    steps: [
        {
            target: '[test-id="create-btn"]',
            title: "Let's create your first pipeline together",
            whatHappens: 'the pipeline builder opens with an empty draft for you to fill in.',
            icon: 'grid',
            description:
                'You are on the <strong>Agents</strong> page. Think of it as the home for ' +
                'every pipeline in your project. A pipeline is what actually answers your users: ' +
                'it takes their question, runs it through an AI model with your instructions, ' +
                'and sends back a reply. Over the next few steps we will build one from scratch, ' +
                'and it takes about five minutes. Click <strong>Create</strong> when you are ' +
                'ready.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
            climbToGroup: true,
        },
        {
            target: '[test-id="name"]',
            title: "First, let's name it",
            whatHappens:
                'GGX asks if it can create the alias from this name. The alias is the short ID ' +
                'used in code, so say yes.',
            icon: 'compass',
            description:
                'Every pipeline needs a name so you and your team can find it later. We will ' +
                'call this one <code data-ggx-copy>Timezone Converter Bot</code>, because that ' +
                'is exactly what it will do: answer questions about time zones. Click the tag to ' +
                'copy the name, paste it into the highlighted field, then click anywhere outside ' +
                'the field.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetMount: true,
            waitCue: 'Enter a name to continue',
        },
        {
            target: '.p-confirmdialog-accept-button',
            targetText: 'Yes, update it',
            title: 'Let GGX set the alias for you',
            whatHappens:
                'the alias fills in, and you move on to choosing how the pipeline is built.',
            icon: 'list',
            description:
                'The alias is a short, code-friendly version of the name, with no spaces or ' +
                'capital letters. It is how your code will refer to this pipeline later. GGX has ' +
                'worked one out from the name you just typed, so you do not have to. Click ' +
                '<strong>Yes, update it</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id^="pipeline-build-type-"]',
            targetText: 'Code Based',
            title: 'How would you like to build it?',
            whatHappens: 'the builder form appears, starting with the pipeline type.',
            icon: 'grid',
            description:
                'There are two ways to build a pipeline. <strong>Code Based</strong> means you ' +
                'write the logic yourself in a few lines of Python, right here in GGX, and you ' +
                'control every detail. <strong>Prebuilt External Agent</strong> connects to an ' +
                'assistant you already run somewhere else. Our bot is small and new, so we will ' +
                'build it here. Click <strong>Code Based</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="pipeline-type-select"]',
            title: 'What kind of conversation will it have?',
            whatHappens: 'the list of four types opens.',
            icon: 'compass',
            description:
                'The pipeline type tells GGX how your bot behaves. <strong>Chat Based</strong> ' +
                'remembers the conversation, so users can ask follow-ups. <strong>Free ' +
                'Form</strong> handles one-off tasks of any kind. ' +
                '<strong>Classification</strong> sorts each input into a category, and ' +
                '<strong>Summarization</strong> shortens a document. People will ask our bot ' +
                "follow-up questions like 'and in Tokyo?', so it needs memory. Click the " +
                '<strong>Pipeline Type</strong> box to open the list.',
            side: 'right',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id^="pipeline-type-option-"]',
            targetText: 'Chat Based Pipeline',
            scrollIntoView: true,
            title: 'Chat Based it is',
            whatHappens:
                'the list closes with Chat Based selected, and a Context Type field appears.',
            icon: 'compass',
            description:
                'Chat Based gives the bot a memory of the conversation so far, which is what ' +
                "lets it understand 'and in Tokyo?' after an earlier question. Click " +
                '<strong>Chat Based Pipeline</strong> in the list.',
            side: 'right',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceWhenAbsent: '.p-select-overlay',
            waitCue: 'Pick Chat Based Pipeline to continue',
        },
        {
            target: '[test-id="context-type"]',
            title: 'What should the bot remember, and how?',
            whatHappens: 'the pipeline keeps the conversation as plain text between messages.',
            icon: 'list',
            description:
                "Context is the bot's memory between messages, and Context Type describes what " +
                'shape that memory takes. For a simple chat, plain text is all we need, and in ' +
                'Python plain text is called <code data-ggx-copy>str</code>. Click the tag to ' +
                'copy it, type it into the <strong>Context Type</strong> field, then click ' +
                'Continue.',
            side: 'top',
            align: 'start',
        },
        {
            target: '[test-id="add-resources-button"]',
            title: "Now let's give it some building blocks",
            whatHappens: 'the Resources section expands with a row for each kind of resource.',
            icon: 'pulse',
            description:
                'Resources are the parts your pipeline can use: AI models, prompts, knowledge ' +
                'sources, other pipelines and helper functions. You attach them here, and then ' +
                'your code calls them by name. Our bot needs two: a model and a prompt. Click ' +
                '<strong>Add Resources</strong>.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="input-llms-select"]',
            title: 'Which model will do the thinking?',
            whatHappens: 'the model list opens with a search box at the top.',
            icon: 'pulse',
            description:
                'The model is the brain of the bot: it reads the question and writes the answer. ' +
                'Converting time zones is a simple job, so a fast, low-cost model is the right ' +
                'choice, and <code data-ggx-copy>gemini_2_5_flash</code> fits well. Click the ' +
                'tag to copy the model name, click the <strong>LLMs / Models</strong> box to ' +
                'open the list, then paste the name into the search to find it.',
            side: 'right',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id^="input-select-option-"]',
            targetText: 'gemini_2_5_flash',
            scrollIntoView: true,
            title: 'There it is',
            whatHappens:
                'the model appears as a chip in the row, and the tour moves on when the list ' +
                'closes.',
            icon: 'pulse',
            description:
                'Click <strong>gemini_2_5_flash</strong> to add it to your pipeline. The list ' +
                'stays open in case you want more than one model. We only need this one, so once ' +
                'it is ticked, click anywhere outside the list to close it.',
            side: 'right',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceWhenAbsent: '.p-multiselect-overlay',
            waitCue: 'Add gemini_2_5_flash, then close the list',
        },
        {
            target: '[test-id="create-new-prompt-button"]',
            title: 'Next, the bot needs instructions',
            whatHappens: 'a panel slides in from the right where you write the new prompt.',
            icon: 'search',
            description:
                'A prompt is the set of instructions the model follows: what it should do, how ' +
                'to answer, what to do when something is unclear. You could reuse a prompt from ' +
                'your project, but our bot needs its own. In the <strong>Prompts</strong> row, ' +
                'click <strong>Create New</strong>.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="create-pipeline-drawer"] [test-id="name"]',
            title: "Let's name the prompt too",
            whatHappens: 'just like before, GGX offers to create the alias from the name.',
            icon: 'search',
            description:
                'Prompts get refined and reused over time, so a clear name helps you find the ' +
                'right one later. We will call this one <code data-ggx-copy>Timezone Converter ' +
                'Bot Prompt</code>. Click the tag to copy it, paste it into the highlighted ' +
                'field, then click anywhere outside the field.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetMount: true,
            waitCue: 'Enter a name to continue',
        },
        {
            target: '.p-confirmdialog-accept-button',
            targetText: 'Yes, update it',
            title: 'Same alias step, same answer',
            whatHappens: 'the alias fills in and you can write the instructions.',
            icon: 'list',
            description:
                'The prompt gets a code-friendly alias too. Your pipeline code will call the ' +
                'prompt by this alias in a few steps, so it is worth accepting the one GGX ' +
                'suggests. Click <strong>Yes, update it</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="create-pipeline-drawer"] [test-id="definition"]',
            title: 'Now, what should the model do?',
            whatHappens:
                "in the next step GGX reads those placeholders and turns them into the prompt's " +
                'inputs.',
            icon: 'search',
            description:
                'This is the prompt itself: the instructions the model reads every time someone ' +
                'asks a question. The parts in curly braces, like {user_message} and ' +
                '{chat_history}, are placeholders. GGX swaps in the real question and the ' +
                'conversation so far each time the bot runs. Copy the prompt below and paste it ' +
                'into the highlighted editor, then click Continue.<pre data-ggx-code data-ggx-wrap>You are a ' +
                'helpful assistant that converts times between time zones.\n' +
                'Task:\n' +
                'Always return the converted time in HH:MM AM/PM format.\n' +
                'Reply "Invalid timezone" if any input or target time zone is invalid.\n' +
                'Assume the input is 12-hour format unless stated otherwise.\n' +
                'Politely ask for clarification if a time zone is unclear.\n' +
                'User Query: {user_message}\n' +
                'Use the chat history to keep context across questions, remembering earlier ' +
                'queries, time zones, and conversions.\n' +
                'Chat History: {chat_history}\n' +
                'Respond first with the converted time clearly labeled by timezone, then a short ' +
                'explanation of the calculation. Avoid extra commentary.</pre>',
            side: 'left',
            align: 'start',
        },
        {
            target: '[test-id="create-pipeline-drawer"] [test-id="fetch-prompt-arguments-button"]',
            targetText: 'Fetch from prompt template',
            title: 'Let GGX read the placeholders for you',
            whatHappens: 'the Arguments list fills in with user_message and chat_history.',
            icon: 'pulse',
            description:
                'Every placeholder in your prompt needs to be declared as an input, so the ' +
                'pipeline knows what to pass in. You could type them by hand, but GGX can read ' +
                'them straight from the template. Click <strong>Fetch from prompt ' +
                'template</strong>.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="create-pipeline-drawer"] [test-id="create-button"]',
            title: 'Your prompt is ready to save',
            whatHappens: 'the panel closes and the new prompt appears in the Prompts row.',
            icon: 'grid',
            description:
                'The prompt has a name, its instructions and its inputs, which is everything it ' +
                'needs. You can always open it later and refine the wording. Click ' +
                '<strong>Create</strong> at the bottom of the panel.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            actionLabel: 'Create',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="pipeline-definition"] .cm-editor',
            waitForAbsent: '[test-id="create-pipeline-drawer"] .p-drawer',
            title: "Now let's wire it all together",
            whatHappens:
                'your bot can take a question, build the instructions, ask the model and return ' +
                'the answer.',
            icon: 'map',
            description:
                'This code box is the heart of the pipeline. It runs every time someone sends a ' +
                'message, and it is where the prompt and the model meet. Right now it holds a ' +
                'placeholder example that has nothing to do with our bot. Clear it out, paste in ' +
                'the three lines below, then click Continue.<pre data-ggx-code>instruction = ' +
                'timezone_converter_bot_prompt(user_message, str(history))\n' +
                'response = gemini_2_5_flash(text=user_message, system_instruction=instruction)\n' +
                'return {"output": response["response"], "context": instruction}</pre>The first ' +
                'line builds the instructions from your prompt and the chat so far. The second ' +
                'sends them to the model. The third returns the reply. If GGX gave your prompt a ' +
                'different alias, change <strong>timezone_converter_bot_prompt</strong> to match ' +
                'it.',
            side: 'left',
            align: 'start',
        },
        {
            target: '[test-id="create-button"]',
            title: "Everything is in place. Let's save it",
            whatHappens: 'the pipeline is saved and its own page opens.',
            icon: 'grid',
            description:
                'Your pipeline now has a name, a type, a model, a prompt and the code that ' +
                'connects them. That is a complete, working pipeline. Click ' +
                '<strong>Create</strong> to register it in your project.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="tab-item-Chats"]',
            scrollIntoView: true,
            title: 'Time to see it work',
            whatHappens:
                'the Chats tab opens and this tour wraps up. The finish screen offers the Live ' +
                'Testing a pipeline tour, which walks you through a whole chat session.',
            icon: 'compass',
            description:
                'The best way to trust a bot is to talk to it, and <strong>Chats</strong> is ' +
                'where you do that: start a session, ask a question and watch how it answers. ' +
                'Rather than leave you at an empty chat, we will finish here and let the Live ' +
                'Testing tour guide you through your first session step by step. When you come ' +
                'back to this bot, ask it <code data-ggx-copy>What is 3 PM New York time in ' +
                "London?</code> and then 'and in Tokyo?' to see the memory you gave it in " +
                'action. Click <strong>Chats</strong> to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
    ],
};
